import { Close, Download, Lock } from "@mui/icons-material";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2,
  IconButton,
  Link,
  Paper,
  Typography,
} from "@mui/material";
import { PMButton, ProgressBarCell } from "@pimo/pimo-components";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { LockDetail } from "@pimo/resource-lock-plugin";
import { Attachment, Configuration, ReportingItem } from "product-test-types";
import { getCustomFieldPublicId } from "product-test-utils";
import React from "react";

import {
  getDropdownColorValue,
  getDropdownLabel,
} from "../utils/dropdown-options";
import { formatDate } from "../utils/format-date";
import { getLockedByMessage } from "../utils/get-locked-by-message";
import { getItemName } from "../utils/get-plural-of-item-name";
import { getProjectName } from "../utils/get-plural-of-project-name";

interface TrackingItemDetailsModalProps {
  open: boolean;
  onClose: () => void;
  selectedTrackingItem?: ReportingItem;
  onEditMeasure?: (trackingItem: ReportingItem) => void;
  onUpdateMeasure?: () => void;
  configuration?: Configuration;
  currentReport?: {
    project?: {
      category?: string;
      id?: string;
      name?: string;
      customId?: string;
    };
  };
  attachments?: Attachment[];
  onDownloadAttachment?: (id: string) => Promise<void>;
  itemLockDetail?: Partial<LockDetail>;
  reportingLockDetail?: Partial<LockDetail>;
  language: SupportedLanguage;
}

export const TrackingItemDetailsModal: React.FC<
  TrackingItemDetailsModalProps
> = ({
  open,
  onClose,
  selectedTrackingItem,
  onEditMeasure,
  onUpdateMeasure,
  configuration,
  currentReport,
  attachments = [],
  onDownloadAttachment,
  reportingLockDetail,
  itemLockDetail,
  language,
}) => {
  if (!selectedTrackingItem) return null;

  // Get the singular item name from configuration
  const itemName = getItemName(configuration?.itemName, language);

  // Get the singular project name from configuration
  const projectName = getProjectName(configuration?.projectName, language);

  const severityColor = getDropdownColorValue(
    configuration?.categoryDefinition,
    currentReport?.project?.category,
    "#e53e3e"
  );

  // Find the color for the item status from statusDefinition
  const statusColor = getDropdownColorValue(
    configuration?.statusDefinition,
    selectedTrackingItem.status,
    "#38a169"
  );

  // Find the color for the item completion from degreeOfCompletionDefinition
  const completionColor = getDropdownColorValue(
    configuration?.degreeOfCompletionDefinition,
    selectedTrackingItem.degreeOfCompletion,
    "#3182ce"
  );

  const dropdownOneColor = getDropdownColorValue(
    configuration?.dropdownFieldOneDefinition,
    selectedTrackingItem.basicItem?.dropdownFieldOne,
    "#805ad5"
  );

  // Helper function to check if a field is enabled
  const isFieldEnabled = (fieldName: string, purpose: string) => {
    return (
      configuration?.fieldsDefinition?.find(
        (field) => field.fieldName === fieldName && field.purpose === purpose
      )?.enabled ?? false
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "none",
          border: "none",
        },
      }}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "white",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          backgroundColor: "white",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {itemName} {getLocalizedMessage(language, "details") || "Details"}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, backgroundColor: "white" }}>
        <Paper sx={{ p: 3, backgroundColor: "white" }}>
          {/* Project ID and Name */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "#666",
                mb: 1,
                fontWeight: "bold",
              }}
            >
              {getLocalizedMessage(language, "idAndName") || "ID and Name"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.875rem",
                fontWeight: "normal",
                overflowWrap: "anywhere",
              }}
            >
              {currentReport?.project?.customId || ""}{" "}
              {currentReport?.project?.name || ""}
            </Typography>
          </Box>
          {/* Description */}
          {isFieldEnabled("description", "itemBasic") && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#666",
                  mb: 1,
                  fontWeight: "bold",
                }}
              >
                {getLocalizedMessage(language, "description") || "Description"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: "normal",
                  overflowWrap: "anywhere",
                }}
              >
                {selectedTrackingItem.basicItem?.description || ""}
              </Typography>
            </Box>
          )}
          <Divider sx={{ mb: 3 }} />
          {/* Key Details Grid2 */}
          <Grid2 container spacing={2} sx={{ mb: 3 }}>
            {isFieldEnabled("dropdownFieldOne", "itemBasic") && (
              <Grid2 size={4}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#666",
                      mb: 1,
                      fontWeight: "bold",
                    }}
                  >
                    {configuration?.fieldsDefinition?.find(
                      (f) => f.fieldName === "dropdownFieldOne"
                    )?.label ??
                      (getLocalizedMessage(language, "category") || "Category")}
                  </Typography>
                  {selectedTrackingItem.basicItem?.dropdownFieldOne ? (
                    <Chip
                      label={getDropdownLabel(
                        configuration?.dropdownFieldOneDefinition,
                        selectedTrackingItem.basicItem.dropdownFieldOne
                      )}
                      sx={{
                        backgroundColor: dropdownOneColor,
                        color: "white",
                        borderRadius: 20,
                      }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.875rem", fontWeight: "normal" }}
                    ></Typography>
                  )}
                </Box>
              </Grid2>
            )}
            {isFieldEnabled("owner", "itemBasic") && (
              <Grid2 size={4}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#666",
                      mb: 1,
                      fontWeight: "bold",
                    }}
                  >
                    {getLocalizedMessage(language, "owner")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.875rem", fontWeight: "normal" }}
                  >
                    {selectedTrackingItem.basicItem?.owner || ""}
                  </Typography>
                </Box>
              </Grid2>
            )}
            {(isFieldEnabled("startDate", "itemBasic") ||
              isFieldEnabled("endDate", "itemBasic")) && (
              <Grid2 size={4}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#666",
                      mb: 1,
                      fontWeight: "bold",
                    }}
                  >
                    Duration
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.875rem", fontWeight: "normal" }}
                  >
                    {selectedTrackingItem.basicItem?.startDate
                      ? formatDate(selectedTrackingItem.basicItem.startDate)
                      : ""}
                    {selectedTrackingItem.basicItem?.startDate &&
                    selectedTrackingItem.basicItem?.endDate
                      ? "-"
                      : ""}
                    {selectedTrackingItem.basicItem?.endDate
                      ? formatDate(selectedTrackingItem.basicItem.endDate)
                      : ""}
                  </Typography>
                </Box>
              </Grid2>
            )}
            {isFieldEnabled("category", "projectBasic") && (
              <Grid2 size={4}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#666",
                      mb: 1,
                      fontWeight: "bold",
                    }}
                  >
                    {getLocalizedMessage(language, "severity") || "Severity"}
                  </Typography>
                  {currentReport?.project?.category ? (
                    <Chip
                      label={getDropdownLabel(
                        configuration?.categoryDefinition,
                        currentReport.project.category
                      )}
                      sx={{
                        backgroundColor: severityColor,
                        color: "white",
                        borderRadius: 20,
                      }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.875rem", fontWeight: "normal" }}
                    ></Typography>
                  )}
                </Box>
              </Grid2>
            )}
            {isFieldEnabled("customId", "projectBasic") && (
              <Grid2 size={4}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#666",
                      mb: 1,
                      fontWeight: "bold",
                    }}
                  >
                    {getLocalizedMessage(language, "parentProjectIdLabel", {
                      projectName,
                    }) || `ID Parent ${projectName}`}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.875rem", fontWeight: "normal" }}
                  >
                    {currentReport?.project?.customId || ""}
                  </Typography>
                </Box>
              </Grid2>
            )}
          </Grid2>
          <Divider sx={{ mb: 3 }} />
          {/* Status and Progress Grid2 */}
          <Grid2 container spacing={2} sx={{ mb: 3 }}>
            {isFieldEnabled("status", "itemReporting") && (
              <Grid2 size={4}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#666",
                      mb: 1,
                      fontWeight: "bold",
                    }}
                  >
                    {getLocalizedMessage(language, "status") || "Status"}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        bgcolor: statusColor,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.875rem", fontWeight: "normal" }}
                    >
                      {getDropdownLabel(
                        configuration?.statusDefinition,
                        selectedTrackingItem.status
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Grid2>
            )}
            {isFieldEnabled("completion", "itemReporting") && (
              <Grid2 size={4}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#666",
                      mb: 1,
                      fontWeight: "bold",
                    }}
                  >
                    {getLocalizedMessage(language, "completion") ||
                      "Completion"}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        bgcolor: completionColor,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.875rem", fontWeight: "normal" }}
                    >
                      {getDropdownLabel(
                        configuration?.degreeOfCompletionDefinition,
                        selectedTrackingItem.degreeOfCompletion
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Grid2>
            )}
            {isFieldEnabled("progress", "itemReporting") && (
              <Grid2 size={4}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "#666", mb: 1, fontWeight: "bold" }}
                  >
                    {getLocalizedMessage(language, "progress") || "Progress"}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        minWidth: "40px",
                        fontSize: "0.875rem",
                        fontWeight: "normal",
                      }}
                    >
                      {selectedTrackingItem.progress
                        ? `${selectedTrackingItem.progress}%`
                        : "0%"}
                    </Typography>
                    <ProgressBarCell
                      header=""
                      progress={selectedTrackingItem.progress ?? 0}
                      progressColor="#2C3E50"
                    />
                  </Box>
                </Box>
              </Grid2>
            )}
            {isFieldEnabled("comment", "itemReporting") && (
              <Grid2 size={4}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "#666", mb: 1, fontWeight: "bold" }}
                  >
                    {getLocalizedMessage(language, "comment") || "Comment"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.875rem", fontWeight: "normal" }}
                  >
                    {selectedTrackingItem.comment || ""}
                  </Typography>
                </Box>
              </Grid2>
            )}
          </Grid2>
          <Divider sx={{ mb: 3 }} />
          <Grid2 container spacing={2}>
            {configuration?.customFields
              ?.filter((f) => f.purpose === "itemReporting" && f.enabled)
              .map((field) => {
                const fieldValue =
                  selectedTrackingItem.customFields?.find(
                    (cf) =>
                      getCustomFieldPublicId(cf.customFieldDefinition) ===
                      getCustomFieldPublicId(field)
                  )?.value || "";
                return (
                  <Grid2 size={4} sx={{ mb: 3 }} key={field.id}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#666",
                        mb: 1,
                        fontWeight: "bold",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {field.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: "normal",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {fieldValue}
                    </Typography>
                  </Grid2>
                );
              })}
          </Grid2>
          {/* Supporting Documents Section */}
          {isFieldEnabled("attachments", "itemReporting") && (
            <>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#666",
                    mb: 2,
                    fontWeight: "bold",
                  }}
                >
                  {getLocalizedMessage(language, "attachments")}
                </Typography>
                {attachments.length > 0 ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {attachments.map((attachment) => (
                      <Link
                        key={attachment.id}
                        component="button"
                        variant="body2"
                        onClick={() => {
                          if (onDownloadAttachment) {
                            onDownloadAttachment(attachment.id);
                          }
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          textAlign: "left",
                          cursor: "pointer",
                          textDecoration: "none",
                          color: "#1976d2",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        <Download sx={{ fontSize: 18 }} />
                        {attachment.originalName}
                      </Link>
                    ))}
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.875rem", color: "#999" }}
                  >
                    {getLocalizedMessage(language, "noDocumentsAvailable") ||
                      "No documents available"}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: "white", gap: 2 }}>
        <PMButton
          size="medium"
          variant={itemLockDetail?.isLocked ? "disabled" : "contained"}
          icon={itemLockDetail?.isLocked ? <Lock /> : undefined}
          tooltip={getLockedByMessage("item", language, itemLockDetail)}
          text={
            getLocalizedMessage(language, "editItem", { itemName }) ||
            `Edit ${itemName}`
          }
          onClick={() => {
            if (onEditMeasure && selectedTrackingItem) {
              onEditMeasure(selectedTrackingItem);
            }
          }}
        />
        {configuration?.isReportingEnabled && (
          <PMButton
            size="medium"
            variant={reportingLockDetail?.isLocked ? "disabled" : "report"}
            icon={reportingLockDetail?.isLocked ? <Lock /> : undefined}
            tooltip={getLockedByMessage(
              "parent-project",
              language,
              reportingLockDetail
            )}
            text={
              getLocalizedMessage(language, "updateItem", { itemName }) ||
              `Update ${itemName}`
            }
            onClick={() => {
              if (onUpdateMeasure) {
                onUpdateMeasure();
              }
            }}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};
